import {
  tBulkMessageContext,
  tCampaignRecipientPreview,
  tCreditRejection,
} from 'src/pages/Pipe/modals/BulkMessageWABModal/BulkMessage.types.ts';
import { useEffect, useMemo, useState } from 'react';
import { tFunnelLead, tPredefinedVariableDefinition, tTemplates } from 'src/pages/Pipe/Pipe.types.ts';
import { PipeService } from 'src/pages/Pipe/Pipe.service.ts';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';
import { usePipeContext } from 'src/pages/Pipe/Pipe.hook.tsx';
import {
  eBulkMessageSteps,
  eOptionSelected,
} from 'src/pages/Pipe/modals/BulkMessageWABModal/BulkMessage.enum.ts';
import { WHATSAPP_TEMPLATE_BODY_MAX_CHARS } from 'src/pages/Pipe/modals/BulkMessageWABModal/BulkMessage.constants.ts';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context.ts';
import { tGenericKeys } from 'src/components/Chat/components/MessageBar/components/WabTemplatesVariablesAction/WabTemplatesVariablesAction.types.ts';
import { buildTemplateMedia } from 'src/components/Chat/components/MessageBar/components/WabTemplatesVariablesAction/WabTemplatesVariablesAction.functions.ts';
import {
  tWapiMedia,
  tWapiTemplateParameter,
} from 'src/components/Chat/Chat.types.ts';
import { WhatsappApiService } from 'src/pages/WhatsappApi/WhatsappApi.service';
import {
  computeHydratedBodyLength,
  parseCreditRejection,
  parseRecipientPreview,
} from 'src/pages/Pipe/modals/BulkMessageWABModal/BulkMessage.functions.ts';
import { useErrorHandler } from 'src/hooks/useErrorHandler.hook';
import { useToastContext } from 'src/providers/ToastProvider/Toast.hook.ts';
import { eAlertType } from 'src/providers/ToastProvider/Toast.enum.ts';
import { useTranslation } from 'react-i18next';
import { useWapiAnalytics } from 'src/hooks/analytics/useWapiAnalytics.hook';
import { WAPI_EVENTS } from 'src/hooks/analytics/wapiAnalytics.constants';
import { templateHeaderRequiresMedia, templateHeaderSendMediaType } from 'src/utils/templateMedia';

export function useBulkMessage(): tBulkMessageContext {
  const { currentWorkspaceId } = useWorkspaceContext();
  const {
    selectedStage,
    setSelectedStage,
    setBulkMessageWABModalIsOpen,
    createCampaignStage,
    createCampaignLeads,
  } = usePipeContext();

  const [number, setNumber] = useState(0);
  const [bulkMessagesLeads, setBulkMessagesLeads] = useState<tFunnelLead[]>([]);
  const [templates, setTemplates] = useState<tTemplates[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [paramValues, setParamValues] = useState<tGenericKeys>({});
  const [imageUrl, setImageUrl] = useState<string>('');
  // Original document filename, kept in lockstep with imageUrl so it is sent to
  // WBS as media.filename for document headers (BUG-475).
  const [imageFileName, setImageFileName] = useState<string>('');
  const [optionSelected, setOptionSelected] = useState<eOptionSelected>(
    eOptionSelected.NUMBER,
  );
  const [selectedTemplate, setSelectedTemplate] = useState<tTemplates | null>(
    null,
  );
  const [variableDefinitions, setVariableDefinitions] = useState<tPredefinedVariableDefinition[]>([]);
  const [step, setStep] = useState<eBulkMessageSteps>(
    eBulkMessageSteps.LEAD_TYPE,
  );
  // BUG-569: backend-resolved recipient/credit numbers for the selected stage.
  // While `recipientPreview` is live it is the modal's single source for both
  // the recipient count and the available credits (superseding the drifted
  // `stages.total_leads` counter and the usageRecord-based credit display).
  const [recipientPreview, setRecipientPreview] =
    useState<tCampaignRecipientPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewStale, setPreviewStale] = useState(false);
  // BUG-569: wba-10024 rejection surfaced inline in the modal body — the
  // modal stays open so the user sees needed-vs-available without a toast.
  const [creditError, setCreditError] = useState<tCreditRejection | null>(
    null,
  );
  const { addAlert } = useToastContext();
  const { t } = useTranslation('whatsappApi/whatsappApi');
  const { t: tErrors } = useTranslation('errors/errors');
  const { trackWapiEvent } = useWapiAnalytics();
  const { handleError } = useErrorHandler();

  /**
   * Length of the selected template's BODY after variable substitution.
   * Single source of truth shared between the char counter in SelectTemplate
   * and the Send button gate in BulkMessageWABModal.
   */
  const hydratedBodyLength = useMemo(
    () => computeHydratedBodyLength(selectedTemplate, paramValues),
    [selectedTemplate, paramValues],
  );

  const isBodyOverLimit = hydratedBodyLength > WHATSAPP_TEMPLATE_BODY_MAX_CHARS;

  /**
   * BUG-847: true when the chosen audience resolves to nobody. Single source
   * of truth shared by the Send/Next button gate, the hint under the lead
   * list, and the defense-in-depth guard in handleSend — the three must agree,
   * or the button re-enables on a state the guard still rejects.
   *
   * Scoped to the LIST path on purpose. The NUMBER path sends to the whole
   * stage and its count is preview-driven, so a zero there can mean "the
   * preview failed" rather than "the stage is empty" — gating on it would
   * block legitimate sends whenever WBS is slow.
   */
  const hasNoRecipients =
    optionSelected === eOptionSelected.LIST && bulkMessagesLeads.length === 0;

  /**
   * Determines the media type based on the file extension
   * @param url - The URL to analyze
   * @returns 'image' | 'video' | 'document'
   */
  const getMediaTypeFromUrl = (url: string): 'image' | 'video' | 'document' => {
    if (!url) return 'image';

    const urlLower = url.toLowerCase();
    const urlWithoutParams = urlLower.split('?')[0];

    if (urlWithoutParams.endsWith('.mp4')) {
      return 'video';
    } else if (
      urlWithoutParams.endsWith('.jpeg') ||
      urlWithoutParams.endsWith('.jpg') ||
      urlWithoutParams.endsWith('.png')
    ) {
      return 'image';
    } else if (urlWithoutParams.endsWith('.pdf')) {
      return 'document';
    }

    return 'image';
  };

  /**
   * Handles sending the bulk message campaign.
   *
   * Validates required fields, sets loading state, and triggers campaign creation for the selected stage and template.
   *
   * @returns {Promise<void>} Resolves when the send operation is complete.
   */
  async function handleSend(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    // Defense-in-depth: Send button is already disabled when over limit, but
    // a pasted value could briefly exceed the cap between render and submit.
    // Block here too and surface a Toast instead of silently hitting Meta.
    if (isBodyOverLimit) {
      addAlert({
        type: eAlertType.ERROR,
        message: t('page.BulkModal.body.previewTemplate.overLimitError', {
          ns: 'pipe/bulk',
        }),
      });
      trackWapiEvent(WAPI_EVENTS.CAMPAIGN_BULK_SEND_ERROR, {
        campaignName: selectedTemplate?.name ?? '',
        errorMessage: 'hydrated_body_over_limit',
        hydratedBodyLength,
      });
      return;
    }

    // BUG-847: same defense-in-depth shape as the guard above — the Send
    // button is already disabled on an empty selection, but nothing stops a
    // submit that races the render. The list path maps `bulkMessagesLeads`
    // straight into the request body, so an empty selection used to reach
    // WBS as `leads: []`, which created a campaign that could never resolve a
    // single contact and sat in «En preparación» forever.
    if (hasNoRecipients) {
      addAlert({
        type: eAlertType.ERROR,
        message: t('page.BulkModal.body.noRecipientsError', {
          ns: 'pipe/bulk',
        }),
      });
      trackWapiEvent(WAPI_EVENTS.CAMPAIGN_BULK_SEND_ERROR, {
        campaignName: selectedTemplate?.name ?? '',
        errorMessage: 'no_recipients_selected',
      });
      return;
    }

    setLoading(true);
    // A new attempt clears the previous rejection from the inline zone.
    setCreditError(null);
    if (!selectedTemplate || !selectedStage) {
      setLoading(false);
      return;
    }

    let requiresHeaderMedia = selectedTemplate.component.startsWith('https://');
    try {
      const components = JSON.parse(selectedTemplate.components);
      if (Array.isArray(components)) {
        requiresHeaderMedia = components.some(
          (component) => component.type === 'HEADER' && templateHeaderRequiresMedia(component.format),
        );
      }
    } catch {
      // Legacy rows only expose the flattened component URL.
    }
    if (requiresHeaderMedia && !imageUrl.trim()) {
      addAlert({ type: eAlertType.ERROR, message: tErrors('wapi.templateHeaderMediaRequired') });
      trackWapiEvent(WAPI_EVENTS.CAMPAIGN_BULK_SEND_ERROR, {
        campaignName: selectedTemplate.name,
        errorMessage: 'template_header_media_required',
      });
      setLoading(false);
      return;
    }

    // `number` is preview-driven when the WBS preview resolved (BUG-569) and
    // falls back to the stage counter otherwise — matching what the user sees.
    const targetUsersCount =
      optionSelected === eOptionSelected.NUMBER
        ? number
        : bulkMessagesLeads.length;

    trackWapiEvent(WAPI_EVENTS.CAMPAIGN_BULK_SEND_START, {
      campaignName: selectedTemplate.name,
      targetUsersCount,
      templateUsed: selectedTemplate.template_id,
    });

    const sendAt = new Date().toISOString();
    const parameters: tWapiTemplateParameter[] = [];
    let media: tWapiMedia | undefined = undefined;

    try {
      const templateComponents = JSON.parse(selectedTemplate.components);
      const templateVarRegex = /{{(.*?)}}/g;
      templateComponents.forEach(
        (component: { type: string; text?: string; format?: string }) => {
          if (
            component.type === 'HEADER' &&
            templateHeaderRequiresMedia(component.format)
          ) {
            const mediaType = templateHeaderSendMediaType(component.format, imageUrl);
            if (!mediaType) return;
            parameters.push({
              name: 'MEDIA',
              value: imageUrl,
              type: 'header',
              format: mediaType,
            });
            media = buildTemplateMedia({
              mediaType,
              url: imageUrl,
              fileName: imageFileName,
            });
          }

          if (component.text) {
            let match;
            const regex = new RegExp(templateVarRegex.source, 'g');

            while ((match = regex.exec(component.text)) !== null) {
              const variableName = match[1].trim();
              const paramType = component.type.toLowerCase(); // "HEADER" -> "header", "BODY" -> "body"

              parameters.push({
                name: variableName,
                value: '',
                type: paramType,
                format: 'text',
              });
            }
          }
        },
      );
    } catch {
      if (selectedTemplate.component.startsWith('https://')) {
        const mediaType = getMediaTypeFromUrl(imageUrl);
        parameters.push({
          name: 'MEDIA',
          value: imageUrl,
          type: 'header',
          format: mediaType,
        });
        media = buildTemplateMedia({
          mediaType,
          url: imageUrl,
          fileName: imageFileName,
        });
      }

      const templateVarRegex = /{{(.*?)}}/g;
      let match;

      while (
        (match = templateVarRegex.exec(selectedTemplate.component)) !== null
      ) {
        const variableName = match[1].trim();
        parameters.push({
          name: variableName,
          value: '',
          type: 'body',
          format: 'text',
        });
      }
    }

    if (Object.keys(paramValues).length > 0) {
      Object.entries(paramValues).forEach(([key, value]) => {
        const param = parameters.find((p) => p.name === key);
        if (param) {
          param.value = value;
        }
      });
    }

    try {
      if (optionSelected === eOptionSelected.NUMBER) {
        await createCampaignStage(
          currentWorkspaceId ?? '',
          selectedTemplate.template_id,
          selectedTemplate.lang,
          selectedStage.id,
          sendAt,
          parameters,
          media,
        );
      } else {
        const leadHandles = bulkMessagesLeads.map((lead) => lead.handle);

        await createCampaignLeads(
          currentWorkspaceId ?? '',
          selectedTemplate.template_id,
          selectedTemplate.lang,
          leadHandles,
          sendAt,
          parameters,
          media,
        );
      }
      // BUG-569: completion analytics and the modal close live in the
      // success path only — campaign creation rethrows on failure, so a
      // rejected send no longer reports success nor dismisses the modal.
      trackWapiEvent(WAPI_EVENTS.CAMPAIGN_BULK_SEND_COMPLETE, {
        campaignName: selectedTemplate.name,
        targetUsersCount,
        templateUsed: selectedTemplate.template_id,
      });
      setBulkMessageWABModalIsOpen(false);
      setSelectedStage(null);
    } catch (error) {
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'BulkMessageWABModal',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error sending campaign',
        error as Error,
      );
      trackWapiEvent(WAPI_EVENTS.CAMPAIGN_BULK_SEND_ERROR, {
        campaignName: selectedTemplate?.name ?? '',
        errorMessage: error instanceof Error ? error.message : String(error),
        targetUsersCount,
      });

      const creditRejection = parseCreditRejection(error);
      if (creditRejection) {
        // wba-10024 → inline error zone is the surface (no toast); the modal
        // stays open so the user sees needed-vs-available and can adjust.
        setCreditError(creditRejection);
      } else {
        // Any other error keeps today's behavior: generic toast + close.
        handleError(
          error,
          optionSelected === eOptionSelected.NUMBER
            ? tErrors('wapi.createStageCampaignError')
            : tErrors('wapi.createLeadsCampaignError'),
        );
        setBulkMessageWABModalIsOpen(false);
        setSelectedStage(null);
      }
    } finally {
      setLoading(false);
    }
  }

  /**
   * Handles the step change in the bulk message modal.
   *
   * Advances the step from lead type selection to template selection, or triggers sending the campaign if on the template step.
   */
  function handleStepChange() {
    if (step === eBulkMessageSteps.LEAD_TYPE) {
      setStep(eBulkMessageSteps.TEMPLATE);
    }
  }

  /**
   * Fetches available templates for the current workspace and updates the state.
   *
   * Handles errors by logging them to the console.
   *
   * @returns {Promise<void>} Resolves when templates are fetched and state is updated.
   */
  async function getTemplates() {
    try {
      setLoadingTemplates(true);
      const response = await WhatsappApiService.fetchTemplates(
        currentWorkspaceId ?? '',
      );
      setTemplates(response.templates);
    } catch (err) {
      console.error('Error fetching templates:', err);
      addAlert({
        type: eAlertType.ERROR,
        message: (err as Error).message || t('page.errors.obtain-error'),
      });
    }
    setLoadingTemplates(false);
  }

  async function getDataBaseTemplates() {
    try {
      setLoadingTemplates(true);
      const [templatesRes, varsRes] = await Promise.all([
        WhatsappApiService.getTemplates(currentWorkspaceId ?? ''),
        PipeService.getPredefinedVariables().catch((err) => {
          LLog.warn(
            { component: LOG_COMPONENTS.UI, method: 'getDataBaseTemplates', operation: LOG_OPERATIONS.ERROR },
            'Failed to fetch predefined variables',
            { error: String(err) },
          );
          return { variables: [] };
        }),
      ]);
      setTemplates(templatesRes.templates);
      setVariableDefinitions(varsRes.variables);
    } catch (err) {
      console.error('Error fetching templates:', err);
      addAlert({
        type: eAlertType.ERROR,
        message: (err as Error).message || t('page.errors.obtain-error'),
      });
    }
    setLoadingTemplates(false);
  }

  useEffect(() => {
    if (selectedStage?.leads) {
      setNumber(selectedStage.leadCount);
    }
  }, [selectedStage]);

  // BUG-569: fetch the backend recipient preview when the modal opens (this
  // provider only mounts while the modal is open) and whenever the selected
  // stage changes. On success the live `requested` count replaces the drifted
  // `selectedStage.leadCount` already seeded into `number` by the effect
  // above; on failure/timeout/unusable payload (old WBS) the counter fallback
  // stays and the count is flagged stale. Sending is never blocked on preview
  // availability — only while the fetch is in flight (bounded by the request
  // timeout in PipeService).
  const previewStageId = selectedStage?.id;
  useEffect(() => {
    if (!previewStageId || !currentWorkspaceId) return;

    let cancelled = false;
    setPreviewLoading(true);
    setPreviewStale(false);
    setRecipientPreview(null);
    setCreditError(null);

    PipeService.getCampaignStageRecipientPreview(
      currentWorkspaceId,
      previewStageId,
    )
      .then((raw) => {
        if (cancelled) return;
        const preview = parseRecipientPreview(raw);
        if (preview) {
          setRecipientPreview(preview);
          setNumber(preview.requested);
        } else {
          // 200 with no usable count (old WBS payload) → counter fallback.
          setPreviewStale(true);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setPreviewStale(true);
        LLog.warn(
          {
            component: LOG_COMPONENTS.UI,
            method: 'useBulkMessage',
            operation: LOG_OPERATIONS.ERROR,
          },
          'Recipient preview unavailable, falling back to stage counter',
          { error: String(err) },
        );
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [previewStageId, currentWorkspaceId]);

  useEffect(() => {
    getDataBaseTemplates();
  }, []);

  return {
    step,
    setStep,
    number,
    setNumber,
    bulkMessagesLeads,
    setBulkMessagesLeads,
    loading,
    setLoading,
    handleStepChange,
    templates,
    setTemplates,
    getTemplates,
    loadingTemplates,
    setLoadingTemplates,
    selectedTemplate,
    setSelectedTemplate,
    handleSend,
    paramValues,
    setParamValues,
    imageUrl,
    setImageUrl,
    imageFileName,
    setImageFileName,
    setOptionSelected,
    optionSelected,
    getMediaTypeFromUrl,
    variableDefinitions,
    hydratedBodyLength,
    isBodyOverLimit,
    hasNoRecipients,
    recipientPreview,
    previewLoading,
    previewStale,
    creditError,
  };
}
